<template>
  <div class="biorhythm-panel-base">
    <template v-if="!errorMessage">
      <div class="type-selector" v-if="isDebug">
        <p>
          <span class="label">グラフ内に表示する数</span>
          <input
            name="target_num"
            type="number"
            v-model="targetNum"
            :min="scoreListMin"
            :max="scoreListMax"
            @change="onParamChange"
          >
        </p>
        <p>
          <span class="label">グラフ内に表示する数</span>
          <select v-model="targetMethod" @change="onMethodChange">
            <option
              v-for="(method, idx) in methodMaster"
              :key="`method${idx}`"
              :value="idx"
            >{{ method }}</option>
          </select>
        </p>
      </div>

      <div class="canvas-wrap" ref="cnvbase">
        <canvas ref="canvas"></canvas>
        <img class="today-label" src="/static/img/recommend/lp/kurenotori/products/biorhythm_todaylabel.svg" alt="">
      </div>

      <div class="score-wrap" v-if="isDebug">
        <div
          v-for="(item, idx) in scoreList"
          :key="idx"
          class="score-item"
        >
          <span
            class="outer-item"
            v-if="idx === 0 || idx === scoreList.length - 1"
          >{{item}}</span>
          <span
            class="inner-item"
            v-else
          ><select @change="onScoreItemChange(idx, $event)">
            <option
              v-for="(score, scidx) in scoreMaster"
              :value="score"
              :key="`${idx}_${scidx}`"
              :selected="item === score"
            >{{score}}</option>
          </select></span>
        </div>
      </div>
    </template>
    <p v-else class="error-message">グラフが描画できません[{{errorMessage}}]</p>
  </div>
</template>

<script>
import moment from 'moment'
import {BIORHYTHM_SCORE_MASTER, BIORHYTHM_METHOD_MASTER} from '../constants'

export default {
  name: 'BiorhythmPanel',
  data(){
    return {
      scoreListMin: 5,
      scoreListMax: 15,
      targetNum: 5,
      targetMethod: 0,
      scoreList: [],
      scoreMaster: BIORHYTHM_SCORE_MASTER,
      methodMaster: BIORHYTHM_METHOD_MASTER,
      imageList: [
        '/static/img/recommend/lp/kurenotori/products/biorhythm_termlabel.svg',
      ],
      loadedImages: [],
      errorMessage: '',
    }
  },
  props: {
    isDebug: {
      type: Boolean,
      default: false,
    },
    scaleUpRatio: {
      type: Number,
      default: 2,
    },
    defTargetNum: {
      type: Number,
      default: 7,
    },
    defTargetMethod: {
      type: Number,
      default: 3,
    },
    termsNum: {
      type: Number,
      default: 6, //カレントは含まない
    },
    blockHeightRatio: {
      type: Number,
      default: 1,
    },
    targetInfo: {
      type: Object,
      default() {
        return {};
      },
    },
    topScore: {
      type: Number,
      default: 100,
    },
    bottomScore: {
      type: Number,
      default: 40,
    },
    resultData: {
      type: Array,
      required: true,
    },
    colorScheme: {
      type: Object,
      default() {
        return {
          lineColor: '#537fe4',
          currentBg: '#fff8c2',
          termsBg: '#e3f7f9',
          outOfTermBg: '#f7f7f7',
          baseBg: 'white',
          ruledLine: 'white',
        };
      },
    },
    lpId: {
      type: Number,
      default: 1,
    },
  },
  watch: {
    targetNum(val, prev) {
      this.targetNum = +val < this.scoreListMin ? this.scoreListMin : +val > this.scoreListMax ? this.scoreListMax : +val
      if(this.targetNum + 2 > this.scoreList.length){
        this.scoreList.push(BIORHYTHM_SCORE_MASTER[parseInt(Math.random() * BIORHYTHM_SCORE_MASTER.length)])
      }else if(this.targetNum + 2 < this.scoreList.length) {
        this.scoreList.pop()
      }
    },
  },
  computed: {
    todaysStr() {
      const m = new moment(this.resultData[1].datetime)
      return m.format('MM.DD')
    },
    resultScoreList() {
      return this.resultData.map(item => +item.text1)
    },
  },
  created() {
    //初回のみprops変数を設定
    this.targetNum = this.defTargetNum
    this.targetMethod = this.defTargetMethod
    if(!this.resultScoreList) {
      for( let i = 0; i < this.targetNum + 2; i++ ){
        this.scoreList.push(BIORHYTHM_SCORE_MASTER[parseInt(Math.random() * BIORHYTHM_SCORE_MASTER.length)])
      }
    } else {
      this.scoreList = [...this.resultScoreList]
    }
    // console.log('resultScoreList[%O]', this.resultScoreList)
  },
  mounted() {
    this.loadImage(this.drawCanvas)
  },
  methods: {
    loadImage(cb){
      Promise.all(this.imageList.map( item => {
        const img = new Image()
        this.loadedImages.push(img)
        img.src = item
        return new Promise((resolve, reject) => {
          img.onload = res => resolve(res)
          img.onerror = err => reject(err)
        })
      }))
        .then(res => this.drawCanvas())
        .catch(err => {
          console.error('Image Load Error %O', err)
          this.errorMessage = "画像ロードできませんでした"
        })
    },
    onParamChange() {
      setTimeout(()=>this.drawCanvas())
    },
    onMethodChange(e){
      setTimeout(()=>this.drawCanvas())
    },
    onScoreItemChange(idx, e) {
      // console.log('かえた！[idx:%s e:%O] 選択したのは[%s] index:%s', idx, e, e.target.value, e.target.selectedIndex)
      this.$set(this.scoreList, idx, +e.target.value)
      setTimeout(()=>this.drawCanvas())
    },
    drawCanvas() {
      let cw, ch, aw, ah, stX, stY
      // console.log('scorelist[ %s ]', this.scoreList)

      const upperLim = 0
      const lowerLim = 0
      const sideMargin = 0

      const hndlColor = '#ff7497'

      // 変数周りvueプロパティで保持する必要なし
      const base = this.$refs.cnvbase

      const baseCnv = this.$refs.canvas
      const baseCtx = baseCnv.getContext('2d')

      const linesCnv = document.createElement('canvas')
      const linesCtx = linesCnv.getContext('2d')

      cw = base.clientWidth * this.scaleUpRatio //グラフ描画エリア幅の確定

      const sep = cw / this.targetNum
      const sepH = sep * this.blockHeightRatio
      const sepHNum = (this.topScore - this.bottomScore) / 10

      ch = sepH * sepHNum //グラフ描画エリア高さの確定

      aw = cw + (cw * sideMargin * 2) //全体幅の確定
      ah = ch + (ch * upperLim) + (ch * lowerLim) //全体高さの確定
      stX = cw * sideMargin
      stY = ch * upperLim

      const footLabel = this.loadedImages[0]
      const footLabelW = sep * 7
      const footLabelH = footLabel.naturalHeight * (sep * 7) / footLabel.naturalWidth

      ah = ah + footLabelH * 0.7

      baseCnv.width = aw
      baseCnv.height = ah  //ベースキャンバスの高さ確定

      // 1%のサイズを設定(50%~100%)
      const onePercentSize = sepH / 10

      // console.log('\n\n\n[canvas初期化]\nwidth:%s height:%s sep:%s sepH:%s sepHNum:%s ', cw, ch, sep, sepH, sepHNum)

      // メインライン描画用キャンバスの初期化（baseCnvより左右２粒多い）
      linesCnv.width = baseCnv.width + (sep * 2)
      linesCnv.height = baseCnv.height

      // メインライン描画用キャンバスの初期化（baseCnvより左右２粒多い）
      linesCnv.width = baseCnv.width + (sep * 2)
      linesCnv.height = baseCnv.height

      // 下地描画
      baseCtx.fillStyle = this.colorScheme.baseBg
      baseCtx.fillRect(0,0, aw, ah)

      // カレント背景の描画
      baseCtx.fillStyle = this.colorScheme.currentBg
      baseCtx.fillRect(stX, stY, sep, ch)

      // 対象期間背景の描画
      baseCtx.fillStyle = this.colorScheme.termsBg
      baseCtx.fillRect(stX + sep, stY, sep * this.termsNum, ch)

      // 期間外背景の描画
      baseCtx.fillStyle = this.colorScheme.outOfTermBg
      baseCtx.fillRect(stX + sep * (this.termsNum + 1), stY, cw - sep * (this.termsNum + 1), ch)

      // 横罫線の描画
      baseCtx.strokeStyle = this.colorScheme.ruledLine
      baseCtx.lineWidth = 1 * this.scaleUpRatio
      for(let i = 0; i < sepHNum - 1; i++ ){
        const lineY = stY + sepH + (sepH * i)
        baseCtx.moveTo(stX, lineY)
        baseCtx.lineTo(stX + cw, lineY)
        baseCtx.stroke()
      }

      // 縦罫線の描画
      for(let i = 0; i < this.targetNum - 1; i++ ){
        const lineX = stX + sep + (sep * i)
        baseCtx.moveTo(lineX, stY)
        baseCtx.lineTo(lineX, stY + ch)
        baseCtx.stroke()
      }

      // console.log('メソッドを確認:[%s]%s', this.targetMethod, BIORHYTHM_METHOD_MASTER[this.targetMethod])

      let lastX, lastY, lastHndlX, lastHndlY
      let bundleProc = false

      //グラフの描画
      this.scoreList.forEach((item, index) => {
        // console.log('item%O', item)
        const posX = index * sep + (sep / 2)
        const posY = onePercentSize * (100 - item)

        switch (+this.targetMethod) {
          case 0: //ポイント描画
            if(this.isDebug) console.log('--- ポイント描画[%s] ---', index)
            linesCtx.fillStyle = this.colorScheme.lineColor
            linesCtx.beginPath()
            linesCtx.arc(posX, posY, 10, 0, Math.PI * 2)
            linesCtx.fill()
            break

          case 1: //棒グラフの描画
            if(this.isDebug) console.log('--- 棒グラフの描画[%s] ---', index)
            linesCtx.fillStyle = this.colorScheme.lineColor
            linesCtx.fillRect(posX - (sep * 0.25), posY, sep * 0.5, ch - posY)
            break

          case 2: //折れ線グラフの描画
            if(this.isDebug) console.log('--- 折れ線グラフの描画[%s] ---', index)
            bundleProc = true
            if(index === 0) {
              linesCtx.strokeStyle = this.colorScheme.lineColor
              linesCtx.lineWidth = 4
              linesCtx.beginPath()
              linesCtx.moveTo(posX, posY)
            } else if(index === this.scoreList.length - 1) {
              linesCtx.lineTo(posX, posY)
              linesCtx.stroke()
            } else {
              linesCtx.lineTo(posX, posY)
            }
            break

          case 3: //３次ベジェ曲線の描画
            if(this.isDebug) console.log('--- ３次ベジェ曲線グラフの描画[%s] ---', index)
            if(index === 0) {
              linesCtx.strokeStyle = this.colorScheme.lineColor
              linesCtx.lineWidth = 4
            } else {
              const currentScore = item
              const nextScore = !!this.scoreList[index + 1] ? this.scoreList[index + 1] : 0
              const nextX = (index + 1) * sep + (sep / 2)
              const nextY = (100 - nextScore) * onePercentSize

              const mainLineAngleRad = Math.atan2(lastY - posY, lastX - posX)
              const nextLineAngleRad = Math.atan2(nextY - posY, nextX - posX)

              let hndlLen, centerRad, absRad, A_bzX, A_bzY, B_bzX, B_bzY
              const _absRad = mainLineAngleRad < 0 ? nextLineAngleRad + (-mainLineAngleRad) : nextLineAngleRad - mainLineAngleRad

              if(Math.abs(_absRad) > Math.PI) {
                absRad = Math.PI * 2 - Math.abs(_absRad)
                centerRad = nextLineAngleRad + (nextLineAngleRad < 0 ? -(absRad / 2) : (absRad / 2))
              } else {
                absRad =  _absRad
                centerRad = absRad / 2 + mainLineAngleRad
              }

              // hndlLen = 60
              hndlLen = sep * 0.4
              const headroom = 5
              const isFlat = (lastY - headroom) <= posY && posY <= (lastY + headroom)
                            || (nextY - headroom) <= posY && posY <= (nextY + headroom)
              const hasLastHndl = !!lastHndlX && !!lastHndlY
              const hndl = {}
              if((centerRad > 0 && centerRad > Math.PI) || centerRad <= 0 && centerRad > -Math.PI) {
                hndl.currentAngle =  centerRad - (Math.PI / 2)
                hndl.nextAngle =  centerRad + (Math.PI / 2)
              } else {
                hndl.currentAngle =  centerRad + (Math.PI / 2)
                hndl.nextAngle =  centerRad - (Math.PI / 2)
              }
              hndl.currentX = isFlat ? posX - hndlLen : Math.cos(hndl.currentAngle) * hndlLen + posX
              hndl.currentY = isFlat ? posY : Math.sin(hndl.currentAngle) * hndlLen + posY
              hndl.nextX = isFlat ? posX + hndlLen : Math.cos(hndl.nextAngle) * hndlLen + posX
              hndl.nextY = isFlat ? posY : Math.sin(hndl.nextAngle) * hndlLen + posY

              A_bzX = hasLastHndl ? lastHndlX : lastX
              A_bzY = hasLastHndl ? lastHndlY : lastY
              B_bzX = hndl.currentX
              B_bzY = hndl.currentY

              if(this.isDebug) {
                console.log(' (%s)->posX:[%s] posY[%s] lastX[%s] lastY[%s]', index, posX, posY, lastX, lastY)
                console.log('       nextX:[%s] nextY[%s]', nextX, nextY)
                console.log('       main[%s] next[%s]', mainLineAngleRad, nextLineAngleRad)
                console.log('       %sabsRad:[%s] _absRad:[%s] centerRad:[%s]', Math.abs(_absRad) > Math.PI ? '*' : '', absRad, _absRad, centerRad)
                console.log('       currentScore[%s] nextScore[%s]', currentScore, nextScore)
                console.log('       lastHndlX[%s] lastHndlY[%s]', lastHndlX, lastHndlY)
                console.log('       isFlat:[%s] hasLastHndl:[%s] A_bzX[%s] A_bxY[%s] B_bzX[%s] B_bzY[%s]', isFlat, hasLastHndl, A_bzX, A_bzY, B_bzX, B_bzY)
              }

              if(this.isDebug) {
                ///// 仮線描画 /////
                linesCtx.strokeStyle = '#aaa'
                linesCtx.lineWidth = 1
                linesCtx.beginPath()
                linesCtx.moveTo(lastX, lastY)
                linesCtx.lineTo(posX, posY)
                linesCtx.stroke()

                const hndlKnob = 10
                linesCtx.strokeStyle = hndlColor
                linesCtx.fillStyle = hndlColor
                // 仮中心線
                linesCtx.beginPath()
                linesCtx.moveTo(posX, posY)
                linesCtx.lineTo(Math.cos(centerRad) * 60 + posX, Math.sin(centerRad) * 60 + posY)
                linesCtx.stroke()

                // 仮ハンドルcurrent
                linesCtx.beginPath()
                linesCtx.moveTo(posX, posY)
                linesCtx.lineTo(B_bzX, B_bzY)
                linesCtx.stroke()
                linesCtx.fillRect(B_bzX - hndlKnob / 2, B_bzY - hndlKnob / 2, hndlKnob, hndlKnob)

                // 仮ハンドルlast
                if (lastHndlX && lastHndlY) {
                  linesCtx.beginPath()
                  linesCtx.moveTo(lastX, lastY)
                  linesCtx.lineTo(A_bzX, A_bzY)
                  linesCtx.stroke()
                  linesCtx.fillRect(A_bzX - hndlKnob / 2, A_bzY - hndlKnob / 2, hndlKnob, hndlKnob)
                }

                //lastむきのほう仮描画
                linesCtx.lineWidth = 3
                const offset = 0
                linesCtx.strokeStyle = '#099'
                linesCtx.beginPath()
                linesCtx.moveTo(posX - offset, posY - offset)
                linesCtx.lineTo(Math.cos(mainLineAngleRad) * 40 + posX - offset, Math.sin(mainLineAngleRad) * 40 + posY - offset)
                linesCtx.stroke()

                //nextむきのほう仮描画
                if (index < this.scoreList.length - 1) {
                  linesCtx.strokeStyle = '#990'
                  linesCtx.beginPath()
                  linesCtx.moveTo(posX + offset, posY + offset)
                  linesCtx.lineTo(Math.cos(nextLineAngleRad) * 40 + posX + offset, Math.sin(nextLineAngleRad) * 40 + posY + offset)
                  linesCtx.stroke()
                }
              }

              ////// 本線描画 /////
              linesCtx.strokeStyle = this.colorScheme.lineColor
              linesCtx.lineWidth = 4 * this.scaleUpRatio
              linesCtx.beginPath()
              linesCtx.moveTo(lastX, lastY)
              linesCtx.bezierCurveTo(A_bzX, A_bzY, B_bzX, B_bzY, posX, posY)
              linesCtx.stroke()

              lastHndlX = hndl.nextX
              lastHndlY = hndl.nextY
            }
            break
        }
        // console.log(  '* lastセット X[%s] Y[%s]\n\n', posX, posY)
        lastX = posX
        lastY = posY
      })

      // 仮のライン描画
      baseCtx.drawImage(linesCnv, sep, 0, cw, ch, stX, stY, cw, ch)

      // カレントの日付を描画
      const fontSize = parseInt(sep / 3)
      baseCtx.font = `bold ${fontSize}px sans-serif`
      baseCtx.fillStyle = this.targetInfo.colorScheme.keyColor
      baseCtx.fillText(this.todaysStr, 0, stY + ch + fontSize + (fontSize * 0.3))

      // ラベルを追加
      baseCtx.drawImage(footLabel,
        0, 0, footLabel.naturalWidth, footLabel.naturalHeight,
        0, ah - footLabelH, footLabelW, footLabelH)

      /*
      // 余白分シャドウ
      baseCtx.fillStyle = 'rgba(0, 0, 0, 0.5)'
      baseCtx.fillRect(0, 0, sep, ch)
      baseCtx.fillRect(cw - sep, 0, sep, ch)
       */
    }
  },
}</script>

<style scoped lang="scss">
@import "../../../../../../sass/commons/commonVariables.scss";

.biorhythm-panel-base {
  margin-bottom: 16px;
  .canvas-wrap {
    width: 100%;
    position: relative;
    canvas {
      display: block;
      width: 100%;
      height: auto;
    }
    .today-label {
      display: block;
      position: absolute;
      width: 17%;
      left: -5.2%;
      top: -16.3%;
      @include mq('w375'){
        width: 14.2%;
        left: -3.3%;
        top: -14.5%;
      }
    }
  }
  .score-wrap {
    padding-top: 24px;
    display: flex;
    width: 100%;
    justify-content: space-around;
  }
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    opacity: 1;
  }
}
.notes {
  font-size: 12px;
  color: red;
}
.error-message {
  text-align: center;
  margin: 0;
  padding: 20px;
}
</style>



// WEBPACK FOOTER //
// ../../../../resources/assets/js/utvStore/components/Products/kurenotori/partials/BiorhythmPanel.vue